import React, { useEffect, useState } from 'react';
import { Box, Card, Typography, TextField, Button, Avatar, Stack, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApiService from '../../../services/api';
import { useNavigate } from 'react-router-dom';

import logger from '../../../utils/logger';
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  padding: '24px'
}));

const ProfileCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  padding: '24px',
  backgroundColor: '#ffffff'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  color: '#0b91d6',
  marginBottom: 16,
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: 6,
}));

const AdminProfile = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let filled = false;
    try {
      const keys = ['auth', 'user', 'admin', 'userdetails'];
      for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        let u = null;
        try { u = JSON.parse(raw); } catch { u = raw; }
        const full_name = u?.full_name || [u?.first_name, u?.last_name].filter(Boolean).join(' ') || u?.user?.full_name || '';
        const email = u?.email || u?.user?.email || '';
        const phone = u?.phone_number || u?.phone || u?.user?.phone_number || u?.user?.phone || '';
        if (full_name || email || phone) {
          setForm((prev) => ({ ...prev, full_name: full_name || prev.full_name, email: email || prev.email, phone: phone || prev.phone }));
          filled = true;
          break;
        }
      }
    } catch {}

    (async () => {
      try {
        const res = await ApiService.get('/admin/auth/get-admin');
        const root = res?.data ?? res;
        const user = root?.data?.admin || root?.admin || root?.data || root;
        const u = Array.isArray(user) ? user[0] : user;
        const full_name = u?.full_name || [u?.first_name, u?.last_name].filter(Boolean).join(' ');
        const email = u?.email || '';
        const phone = u?.phone_number || u?.phone || '';
        // Only update if not already set or values are empty
        setForm((prev) => ({
          ...prev,
          full_name: prev.full_name || full_name || '',
          email: prev.email || email || '',
          phone: prev.phone || phone || '',
        }));
      } catch (e) {
        // ignore; keep whatever we have
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess('');
    setError('');
    try {
      // Derive current admin id
      let id = null;
      try {
        const authRaw = localStorage.getItem('auth') || localStorage.getItem('user');
        if (authRaw) {
          const auth = JSON.parse(authRaw);
          id = auth?.id || auth?.user?.id || auth?.data?.id || null;
        }
      } catch {}
      if (!id) {
        try {
          const who = await ApiService.get('/admin/auth/get-admin');
          const root = who?.data ?? who;
          const u = root?.data?.admin || root?.admin || root?.data || root;
          const cand = Array.isArray(u) ? u[0] : u;
          id = cand?.id || null;
        } catch {}
      }

      // Fallback to provided example id if still missing
      if (!id) id = 16;

      const payload = {
        full_name: form.full_name?.trim(),
        email: form.email?.trim(),
        phone_number: form.phone?.trim(),
      };
      // Remove empty values
      Object.keys(payload).forEach((k) => (payload[k] === '' || payload[k] == null) && delete payload[k]);

      const res = await ApiService.put(`/admin/auth/profile-update/${id}`, payload);
      const msg = res?.data?.message || res?.message || 'Profile updated successfully';
      // If phone_number was submitted, backend now creates a pending request instead of applying immediately
      if (payload.phone_number) {
        setSuccess('Profile updated. Phone change will be applied after admin approval.');
      } else {
        setSuccess(msg);
      }

      // Update local cache for header/display
      try {
        const stored = localStorage.getItem('auth');
        if (stored) {
          const auth = JSON.parse(stored);
          const updated = {
            ...auth,
            full_name: payload.full_name || auth.full_name,
            email: payload.email || auth.email,
            // Do not overwrite phone locally when a phone change was requested — it awaits admin approval
            phone: payload.phone_number ? auth.phone : (payload.phone_number || auth.phone),
            user: {
              ...(auth.user || {}),
              full_name: payload.full_name || auth.user?.full_name,
              email: payload.email || auth.user?.email,
              phone: payload.phone_number ? auth.user?.phone : (payload.phone_number || auth.user?.phone),
            },
          };
          localStorage.setItem('auth', JSON.stringify(updated));
        }
      } catch {}
    } catch (err) {
      const raw = err?.response?.data || err?.body || err;
      const msg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || 'Failed to update profile');
      setError(msg);
      logger.error('Failed to submit profile', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, color: '#374151' }}>Back</Typography>
      </Box>

      <ProfileCard>
        <SectionTitle>Profile</SectionTitle>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={2}>
            <Stack direction="row" alignItems="center" justifyContent="center">
              <Avatar src="/images/avatar-placeholder.png" sx={{ width: 96, height: 96 }} />
            </Stack>
          </Grid>

          <Grid item xs={12} md={10}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <Label>Name</Label>
                  <TextField
                    fullWidth
                    size="small"
                    name="full_name"
                    value={form.full_name}
                    onChange={handleChange}
                    placeholder="Your name"
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Label>Email</Label>
                  <TextField
                    fullWidth
                    size="small"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Label>Contact Number</Label>
                  <TextField
                    fullWidth
                    size="small"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter contact number"
                  />
                </Grid>

                {error && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="error">{error}</Typography>
                  </Grid>
                )}
                {success && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="success.main">{success}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button type="submit" variant="contained" disabled={submitting} sx={{ textTransform: 'none', bgcolor: '#0b91d6', '&:hover': { bgcolor: '#0a7db7' } }}>
                      {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </ProfileCard>
    </PageContainer>
  );
};

export default AdminProfile;
